import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coffee, Gift, UtensilsCrossed, QrCode, Users, Sparkles, ArrowRight, Star } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

const STEPS = [
  { icon: QrCode, t: "Registrieren", d: "Kostenlos anmelden und persönlichen QR-Code erhalten." },
  { icon: Coffee, t: "Punkte sammeln", d: "QR-Code an der Kasse zeigen und bei jedem Einkauf sammeln." },
  { icon: Gift, t: "Prämien einlösen", d: "Punkte gegen gratis Kaffee, Snacks und Menüs eintauschen." },
];

export default function HeliumVorteile() {
  const [rewards, setRewards] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/rewards").then((r) => setRewards(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);
  const l = settings?.loyalty || {};

  return (
    <div>
      <Seo title="Helium Club – Das Treueprogramm" description="Sammle Punkte bei jedem Einkauf und sichere dir gratis Kaffee, Snacks und Menüs. Jetzt kostenlos Mitglied im Helium Club werden." />
      <section className="bg-gradient-to-br from-[#1F2937] to-[#111827] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-[#E0243A] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">Helium Club</span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold font-display leading-tight">Jeder Besuch<br />wird belohnt.</h1>
            <p className="mt-4 text-gray-300 max-w-lg">Werde Teil des Helium Clubs und sammle bei jedem Einkauf Punkte. €1 = {l.points_per_euro || 1} Punkt. Einlösen: Preis × {l.redeem_multiplier || 40}, gerundet auf {l.redeem_round_step || 10} Punkte. Als Neukunde erhältst du {l.welcome_points || 120} Willkommenspunkte – genug für deinen ersten Gratis-Kaffee.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/registrieren" data-testid="vorteile-register-cta" className="inline-flex items-center gap-2 bg-[#E0243A] hover:bg-[#C81E33] px-6 py-3.5 rounded-xl font-semibold">Jetzt kostenlos beitreten <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/mein-konto" className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 px-6 py-3.5 rounded-xl font-semibold">Mein QR-Code</Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[{ i: Coffee, t: "Gratis Kaffee", p: 500 }, { i: UtensilsCrossed, t: "Gratis Snack", p: 1000 }, { i: Gift, t: "Gratis Menü", p: 1500 }].map((r, i) => {
              const Icon = r.i;
              return <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center"><Icon className="w-8 h-8 mx-auto text-[#E0243A]" /><div className="mt-2 font-semibold text-sm">{r.t}</div><div className="text-xs text-gray-400">{r.p} Pkt.</div></div>;
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="text-2xl sm:text-3xl font-bold text-center">So einfach geht's</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-8 text-center shadow-sm">
                <div className="w-14 h-14 rounded-2xl bg-[#FDF2F4] flex items-center justify-center mx-auto"><Icon className="w-7 h-7 text-[#E0243A]" /></div>
                <div className="mt-4 text-sm text-[#E0243A] font-semibold">Schritt {i + 1}</div>
                <h3 className="text-lg font-semibold mt-1">{s.t}</h3>
                <p className="text-sm text-gray-600 mt-2">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F9F8F6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8">Verfügbare Prämien</h2>
          {rewards.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rewards.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <div className="h-40"><img src={r.image} alt={r.name} className="w-full h-full object-cover" /></div>
                  <div className="p-5 flex items-center justify-between">
                    <div><h3 className="font-semibold">{r.name}</h3><p className="text-sm text-gray-500">{r.description}</p></div>
                    <span className="flex items-center gap-1 bg-[#FDF2F4] text-[#C81E33] px-3 py-1 rounded-full text-sm font-bold"><Star className="w-4 h-4" />{r.points_required}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center" data-testid="rewards-coming-soon">
              <div className="inline-flex items-center gap-2 bg-[#E0243A]/15 text-[#E0243A] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E0243A] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#E0243A]" /></span>
                Demnächst
              </div>
              <h3 className="mt-4 text-xl font-bold">Unsere Prämien kommen bald!</h3>
              <p className="mt-2 text-gray-600 max-w-md mx-auto">Wir schnüren gerade attraktive Prämien für dich. Registriere dich jetzt, sammle schon bald Punkte und sei von Anfang an dabei.</p>
              <Link to="/registrieren" className="mt-6 inline-flex items-center gap-2 bg-[#E0243A] hover:bg-[#C81E33] text-white px-6 py-3 rounded-xl font-semibold">Jetzt vormerken <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-3xl border border-gray-100 p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center shadow-sm">
          <div>
            <div className="w-12 h-12 rounded-xl bg-[#FDF2F4] flex items-center justify-center"><Users className="w-6 h-6 text-[#E0243A]" /></div>
            <h2 className="text-2xl sm:text-3xl font-bold mt-4">Freunde einladen, doppelt profitieren</h2>
            <p className="text-gray-600 mt-2">Empfehle HELIUM weiter. Sobald sich dein Freund mit deinem Empfehlungscode registriert, erhaltet ihr beide {l.referral_points || 200} Bonuspunkte.</p>
            <Link to="/mein-konto" className="mt-5 inline-flex items-center gap-2 font-semibold text-[#E0243A]">Meinen Empfehlungscode ansehen <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] rounded-2xl p-8 text-white text-center">
            <Sparkles className="w-10 h-10 mx-auto text-[#E0243A]" />
            <div className="mt-3 text-3xl font-extrabold font-display">+{l.referral_points || 200}</div>
            <div className="text-gray-400 text-sm">Punkte pro Empfehlung – für euch beide</div>
          </div>
        </div>
      </section>
    </div>
  );
}
